import React, { useContext, useMemo } from 'react';
import { UNSAFE_NavigationContext, UNSAFE_RouteContext, useLocation } from 'react-router';

// The one place components take router props from (PANEL-1548). react-router 6+ has no withRouter, so
// since the upgrade (PANEL-1549) this builds react-router 5's props from react-router 8:
// - history: react-router 5's API over the router's navigator (the app's history object, see
//   AppRouter). The navigator is used rather than useNavigate(), whose navigate() is ignored until
//   the component calling it has run its layout effects: a class page that redirects in
//   componentDidMount (GDPR) would be left where it was.
// - location: the router's location (pathname, search, hash, state, key), with react-router 5's
//   `state: undefined` where react-router 8 says null: GDPR tells "opened directly" from "opened with
//   records" by `state !== undefined`, and with null it crashed instead of redirecting home.
// - match: react-router 5's { path, url, isExact, params } of the closest route, from react-router 8's
//   route match: the pattern without the /* that stands for react-router 5's prefix matching, the
//   matched part of the URL, and the params without that splat. Outside any route: react-router 5's
//   root match.

// react-router 5 accepted a location object with the state inside it (which wins over a second
// argument, as in history 4), and a partial location that keeps the current pathname; react-router
// 8's history takes the state as its own argument. The arguments are passed on as given otherwise
// (no trailing undefined state).
const historyArgs = (to, ...state) => {
  if (to && typeof to === 'object' && 'state' in to) {
    const { state: inner, ...path } = to;
    return inner === undefined ? [path, ...state] : [path, inner];
  }
  return [to, ...state];
};

const v5Locations = new WeakMap();
const v5Location = (location) => {
  if (location.state !== null) return location;
  if (!v5Locations.has(location)) v5Locations.set(location, { ...location, state: undefined });
  return v5Locations.get(location);
};

// One react-router 5 history per navigator, shared by every consumer, as react-router 5's single history
// object was (specs spy on the one they capture).
const v5Histories = new WeakMap();
const createV5History = (navigator) => ({
  push: (...args) => navigator.push(...historyArgs(...args)),
  replace: (...args) => navigator.replace(...historyArgs(...args)),
  go: (delta) => navigator.go(delta),
  goBack: () => navigator.go(-1),
  goForward: () => navigator.go(1),
  createHref: (to) => navigator.createHref(to),
  get location() {
    return v5Location(navigator.location);
  },
  // A memory history's stack, as history 4 exposed it (the specs' routers).
  get entries() {
    return navigator.entries;
  },
  get index() {
    return navigator.index;
  },
  get action() {
    return navigator.action;
  },
  get length() {
    return navigator.length === undefined ? window.history.length : navigator.length;
  },
  // No listen(): react-router 8's history keeps one listener, the router's, and a second would
  // replace it. Nothing in the app listens.
});
const v5History = (navigator) => {
  if (!v5Histories.has(navigator)) v5Histories.set(navigator, createV5History(navigator));
  return v5Histories.get(navigator);
};

const v5Match = (routeMatch, pathname) => {
  if (!routeMatch) return { path: '/', url: '/', isExact: pathname === '/', params: {} };
  const { '*': splat, ...params } = routeMatch.params;
  const url = routeMatch.pathnameBase;
  return {
    path: (routeMatch.route.path || '/').replace(/\/\*$/, '') || '/',
    url,
    isExact: pathname === url || pathname === `${url}/`,
    params,
  };
};

export function useRouterProps() {
  const navigation = useContext(UNSAFE_NavigationContext);
  if (!navigation) throw new Error('You should not use withRouter outside a <Router>');
  const { navigator } = navigation;
  const { matches } = useContext(UNSAFE_RouteContext);
  const location = v5Location(useLocation());
  const history = useMemo(() => v5History(navigator), [navigator]);
  const routeMatch = matches[matches.length - 1];
  const match = useMemo(() => v5Match(routeMatch, location.pathname), [routeMatch, location.pathname]);
  return { history, location, match };
}

export function withRouter(Component) {
  function WithRouter(props) {
    return <Component {...props} {...useRouterProps()} />;
  }
  WithRouter.displayName = `withRouter(${Component.displayName || Component.name || 'Component'})`;
  WithRouter.WrappedComponent = Component;
  return WithRouter;
}
